import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { backupApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  Download, 
  Upload, 
  Database, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  FileJson,
  HardDrive,
  Shield
} from "lucide-react";

// Friendly names for collections
const COLLECTION_NAMES = {
  users: "Usuarios",
  patients: "Pacientes",
  studies: "Estudios",
  analytes: "Analitos",
  study_analytes: "Analitos por Estudio",
  analyte_formulas: "Analitos Calculados",
  areas: "Áreas",
  sample_types: "Tipos de Muestra",
  containers: "Recipientes",
  techniques: "Técnicas",
  methods: "Métodos",
  doctors: "Doctores",
  companies: "Compañías",
  price_lists: "Listas de Precios",
  study_prices: "Precios de Estudios",
  profiles: "Perfiles",
  profile_prices: "Precios de Perfiles",
  work_orders: "Órdenes de Trabajo",
  results: "Resultados",
  counters: "Contadores",
  branches: "Sucursales",
  roles: "Roles",
};

export default function Backup() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await backupApi.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await backupApi.export();
      
      // Create download link
      const blob = new Blob([response.data], { type: "application/gzip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with date
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const time = new Date().toTimeString().slice(0, 5).replace(":", "");
      link.download = `laboratorio_backup_${date}_${time}.json.gz`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setLastBackup(new Date().toLocaleString());
      toast.success("Backup descargado correctamente");
    } catch (error) {
      toast.error("Error al exportar datos");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!name.endsWith(".json") && !name.endsWith(".gz")) {
      toast.error("Por favor selecciona un archivo .json o .json.gz válido");
      return;
    }

    if (
      !confirm(
        `¿Estás seguro de importar este backup?\n\nArchivo: ${file.name}\nTamaño: ${(file.size / 1024 / 1024).toFixed(2)} MB\n\n⚠️ Esto reemplazará los datos actuales.`
      )
    ) {
      event.target.value = "";
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await backupApi.import(formData);
      setImportResult(response.data);
      toast.success("Backup importado correctamente");

      // Refresh stats
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar backup");
      console.error(error);
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  const getTotalRecords = () => {
    return Object.values(stats).reduce((sum, count) => sum + count, 0);
  };

  const getActiveCollections = () => {
    return Object.values(stats).filter((count) => count > 0).length;
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Respaldo y Restauración
        </h1>
        <p className="text-muted-foreground">
          Descarga o restaura los datos de tu sistema de laboratorio
        </p>
      </div>

      {/* Info Alert */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertTitle>Importante</AlertTitle>
        <AlertDescription>
          Te recomendamos descargar un respaldo regularmente, especialmente antes de cambiar de chat o hacer cambios importantes. 
          El archivo JSON contiene todos tus catálogos, pacientes, órdenes y configuraciones.
        </AlertDescription>
      </Alert>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-green-600" />
              Descargar Respaldo
            </CardTitle>
            <CardDescription>
              Descarga todos los datos en un archivo JSON
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">{getTotalRecords()} registros</p>
                <p className="text-sm text-muted-foreground">
                  en {getActiveCollections()} colecciones
                </p>
              </div>
              <Database className="h-8 w-8 text-muted-foreground" />
            </div>
            
            <Button 
              onClick={handleExport} 
              disabled={exporting}
              className="w-full"
              size="lg"
            >
              {exporting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar Backup
                </>
              )}
            </Button>

            {lastBackup && (
              <p className="text-sm text-center text-muted-foreground">
                Último backup: {lastBackup}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Import Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Restaurar Respaldo
            </CardTitle>
            <CardDescription>
              Carga un archivo JSON para restaurar datos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Archivo .json</p>
                <p className="text-sm text-muted-foreground">
                  Generado por este sistema
                </p>
              </div>
              <FileJson className="h-8 w-8 text-muted-foreground" />
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".json,.gz,.json.gz,application/gzip"
              className="hidden"
            />

            <Button 
              onClick={handleImportClick} 
              disabled={importing}
              variant="outline"
              className="w-full"
              size="lg"
            >
              {importing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Seleccionar Archivo
                </>
              )}
            </Button>

            {importResult && (
              <Alert variant="default" className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Importación exitosa</AlertTitle>
                <AlertDescription className="text-green-700">
                  Se importaron {Object.values(importResult.imported_counts || {}).reduce((a, b) => a + b, 0)} registros
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Current Data Stats */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Datos Actuales
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchStats}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Cargando estadísticas...
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Object.entries(COLLECTION_NAMES).map(([key, name]) => (
                <div
                  key={key}
                  className="p-3 border rounded-lg text-center hover:bg-muted/50 transition-colors"
                >
                  <p className="text-2xl font-bold">{stats[key] || 0}</p>
                  <p className="text-xs text-muted-foreground">{name}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Details */}
      {importResult?.imported_counts && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Detalle de Importación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Colección</TableHead>
                  <TableHead className="text-right">Registros Importados</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(importResult.imported_counts).map(([key, count]) => (
                  <TableRow key={key}>
                    <TableCell>{COLLECTION_NAMES[key] || key}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={count > 0 ? "default" : "secondary"}>
                        {count}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {importResult.errors?.length > 0 && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Algunos errores ocurrieron</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
